import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Zap, CheckCircle, XCircle, AlertCircle, ExternalLink,
  Database, Search, Key, Mail, Construction, Settings,
} from 'lucide-react';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';

// ─── shared helpers ───────────────────────────────────────────────

const ComingSoonTab = ({ icon: Icon, title, description }) => (
  <Card>
    <CardContent className="py-16">
      <div className="text-center text-gray-500">
        <Construction className="w-14 h-14 mx-auto mb-4 text-gray-300" />
        <h3 className="text-base font-semibold text-gray-700 mb-2">Coming Soon</h3>
        <p className="text-sm">{description}</p>
        <p className="text-xs text-gray-400 mt-2">Backend integration pending</p>
      </div>
    </CardContent>
  </Card>
);

// ─── Feature Flags tab ────────────────────────────────────────────

const DEFAULT_FEATURES = [
  { key: 'sales_enabled', label: 'Sales' },
  { key: 'purchases_enabled', label: 'Purchases' },
  { key: 'catalogue_enabled', label: 'Catalogue' },
  { key: 'cash_bank_enabled', label: 'Cash & Bank' },
  { key: 'gst_enabled', label: 'GST' },
  { key: 'reports_enabled', label: 'Reports' },
  { key: 'pos_enabled', label: 'POS' },
  { key: 'field_tracking_enabled', label: 'Field Tracking' },
  { key: 'attendance_enabled', label: 'Attendance' },
  { key: 'visit_tasks_enabled', label: 'Visit Tasks' },
  { key: 'collections_enabled', label: 'Collections' },
  { key: 'routes_enabled', label: 'Routes' },
];

const keyToLabel = (key) =>
  key.replace(/_enabled$/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const deriveFeaturesFromPlans = (plans) => {
  if (!plans || plans.length === 0) return DEFAULT_FEATURES;
  const enabledKeys = new Set();
  plans.forEach(plan => {
    Object.keys(plan).forEach(key => {
      if (key.endsWith('_enabled') && typeof plan[key] === 'boolean') enabledKeys.add(key);
    });
  });
  if (enabledKeys.size === 0) return DEFAULT_FEATURES;
  const defaultKeys = new Set(DEFAULT_FEATURES.map(f => f.key));
  const ordered = DEFAULT_FEATURES.filter(f => enabledKeys.has(f.key));
  enabledKeys.forEach(key => {
    if (!defaultKeys.has(key)) ordered.push({ key, label: keyToLabel(key) });
  });
  return ordered.length > 0 ? ordered : DEFAULT_FEATURES;
};

const FeatureFlagsTab = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState(DEFAULT_FEATURES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/admin/plans-v2/plans', { params: { include_inactive: true } })
      .then(res => {
        const data = res.data;
        const planList = Array.isArray(data) ? data : (data.plans || []);
        setPlans(planList);
        setFeatures(deriveFeaturesFromPlans(planList));
      })
      .catch(err => setError(getApiErrorMessage(err, 'Failed to load plans')))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-[3px] border-slate-200 border-t-yellow-500 rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <AlertCircle className="w-10 h-10 text-red-400" />
      <p className="text-gray-600">{error}</p>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-600" />
            <CardTitle>Feature Matrix by Plan</CardTitle>
          </div>
          <button
            onClick={() => navigate('/admin/plans-v2')}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm font-medium"
          >
            <ExternalLink className="w-4 h-4" />
            Edit Plans
          </button>
        </div>
        <p className="text-sm text-gray-500 mt-1">Feature availability per plan (read-only view)</p>
      </CardHeader>
      <CardContent>
        {plans.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No plans found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-medium text-gray-600 min-w-[140px]">Feature</th>
                  {plans.map(plan => (
                    <th key={plan.id} className="text-center p-3 font-medium text-gray-800 min-w-[100px]">
                      {plan.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {features.map(feature => (
                  <tr key={feature.key} className="border-b hover:bg-gray-50">
                    <td className="p-3 text-gray-700 font-medium">{feature.label}</td>
                    {plans.map(plan => (
                      <td key={plan.id} className="p-3 text-center">
                        {plan[feature.key]
                          ? <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                          : <XCircle className="w-5 h-5 text-gray-300 mx-auto" />
                        }
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Business Limits tab ──────────────────────────────────────────

const PAGE_SIZE = 20;

const BusinessLimitsTab = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
    if (searchDebounced) params.search = searchDebounced;
    api.get('/admin/businesses', { params })
      .then(res => {
        const data = res.data;
        setBusinesses(Array.isArray(data) ? data : (data.businesses || []));
        setTotal(data.total ?? (Array.isArray(data) ? data.length : 0));
      })
      .catch(err => setError(getApiErrorMessage(err, 'Failed to load businesses')))
      .finally(() => setLoading(false));
  }, [page, searchDebounced]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <AlertCircle className="w-10 h-10 text-red-400" />
      <p className="text-gray-600">{error}</p>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-600" />
            <CardTitle>All Businesses ({total})</CardTitle>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search businesses..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-1">View plan and subscription status per business. Click a row to manage limits.</p>
      </CardHeader>
      <CardContent>
        {businesses.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No businesses found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col className="w-[30%]" />
                <col className="w-[15%]" />
                <col className="w-[12%]" />
                <col className="w-[18%]" />
                <col className="w-[18%]" />
                <col className="w-[7%]" />
              </colgroup>
              <thead>
                <tr className="border-b sticky top-0 bg-white">
                  <th className="text-left p-3 font-medium text-gray-600">Business</th>
                  <th className="text-left p-3 font-medium text-gray-600">Plan</th>
                  <th className="text-left p-3 font-medium text-gray-600">Status</th>
                  <th className="text-left p-3 font-medium text-gray-600">Subscription</th>
                  <th className="text-left p-3 font-medium text-gray-600">Created</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {businesses.map(business => (
                  <tr
                    key={business.id}
                    className="border-b hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/admin/businesses/${business.id}`)}
                  >
                    <td className="p-3">
                      <div className="font-medium text-gray-900">{business.name}</div>
                      <div className="text-xs text-gray-400">{business.owner_email || '—'}</div>
                    </td>
                    <td className="p-3 text-gray-700">
                      {business.plan_name || business.plan?.name || '—'}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        business.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {business.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        business.subscription_status === 'active' ? 'bg-blue-100 text-blue-700'
                        : business.subscription_status === 'trialing' ? 'bg-amber-100 text-amber-700'
                        : 'bg-gray-100 text-gray-600'
                      }`}>
                        {business.subscription_status || 'N/A'}
                      </span>
                    </td>
                    <td className="p-3 text-gray-500 text-xs">
                      {business.created_at ? new Date(business.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="p-3">
                      <ExternalLink className="w-4 h-4 text-gray-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4 border-t mt-4">
            <p className="text-sm text-gray-500">
              Page {page + 1} of {totalPages} ({total} total)
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Main page ────────────────────────────────────────────────────

const TABS = [
  { id: 'features', label: 'Feature Flags', icon: Zap, ready: true },
  { id: 'limits', label: 'Business Limits', icon: Database, ready: true },
  { id: 'integrations', label: 'Integrations', icon: Key, ready: false },
  { id: 'providers', label: 'Providers', icon: Mail, ready: false },
];

const SystemConfig = () => {
  const [activeTab, setActiveTab] = useState('features');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Settings className="w-6 h-6 text-gray-700" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Config</h1>
          <p className="text-gray-600 text-sm mt-0.5">Platform-wide configuration and feature management</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-[13px] font-medium flex items-center gap-2 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'text-rose-600 border-rose-600'
                    : 'text-slate-500 border-transparent hover:text-slate-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {!tab.ready && (
                  <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-normal">
                    Soon
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'features' && <FeatureFlagsTab />}
      {activeTab === 'limits' && <BusinessLimitsTab />}
      {activeTab === 'integrations' && (
        <ComingSoonTab
          icon={Key}
          title="Integrations"
          description="Configure API keys and third-party integrations"
        />
      )}
      {activeTab === 'providers' && (
        <ComingSoonTab
          icon={Mail}
          title="Providers"
          description="Manage Email, SMS, and WhatsApp communication providers"
        />
      )}
    </div>
  );
};

export default SystemConfig;
